/**
 * storageQuota.ts — localStorage Quota Safety
 *
 * Provides utilities to check remaining localStorage capacity
 * and handle quota exceeded errors gracefully.
 *
 * Addresses: PRODUCT_UX_REPORT §1.5 QA "localStorage quota exceeded — No handling"
 */

import { warn, error as logError } from './logger';

/**
 * Estimated max localStorage size (5MB for most browsers).
 *
 * `STORAGE_QUOTA_MB` is exported because the admin portal shows this number to a
 * human ("3.1 / 5 MB") and computes a fill percentage from it. Those sites used
 * to divide by a literal `5`, so tuning the assumption here would silently leave
 * two admin screens quoting the old ceiling — while still looking authoritative.
 */
export const STORAGE_QUOTA_MB = 5;
const ESTIMATED_MAX_BYTES = STORAGE_QUOTA_MB * 1024 * 1024;

/**
 * Get the current total size of all localStorage data in bytes.
 */
export function getLocalStorageUsedBytes(): number {
  let total = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        // Each char is 2 bytes in UTF-16
        total += (key.length + (localStorage.getItem(key)?.length ?? 0)) * 2;
      }
    }
  } catch (err) {
    logError('Failed to calculate localStorage size:', err);
  }
  return total;
}

/**
 * Get estimated remaining localStorage capacity in bytes.
 */
export function getLocalStorageRemainingBytes(): number {
  return Math.max(0, ESTIMATED_MAX_BYTES - getLocalStorageUsedBytes());
}

/**
 * Get localStorage usage as a percentage (0-100).
 */
export function getLocalStorageUsagePercent(): number {
  return Math.round(getLocalStorageUsedBytes() / ESTIMATED_MAX_BYTES * 100);
}

/**
 * Check if localStorage is nearing capacity (>80% full).
 */
export function isLocalStorageNearFull(): boolean {
  return getLocalStorageUsagePercent() > 80;
}

/**
 * Safe localStorage write with quota check.
 * Returns true if write succeeded, false if quota would be exceeded.
 *
 * If the write would push usage above 90%, it logs a warning.
 * If the write fails due to QuotaExceededError, it returns false.
 */
export function safeStorageWrite(key: string, value: string): boolean {
  try {
    // Pre-check: estimate if this write would exceed quota
    const newDataSize = (key.length + value.length) * 2;
    const currentUsed = getLocalStorageUsedBytes();
    const projectedUsage = currentUsed + newDataSize;
    if (projectedUsage > ESTIMATED_MAX_BYTES * 0.9) {
      warn(`localStorage nearing capacity: ${Math.round(projectedUsage / ESTIMATED_MAX_BYTES * 100)}% after write to "${key}" (${Math.round(newDataSize / 1024)}KB)`);
    }
    localStorage.setItem(key, value);
    return true;
  } catch (err: unknown) {
    if (err instanceof DOMException && (err.name === 'QuotaExceededError' || err.code === 22 || err.code === 1014)) {
      warn(`localStorage quota exceeded when writing "${key}" (${Math.round(value.length * 2 / 1024)}KB). Attempting eviction...`);
      // H-4: Attempt automatic eviction and retry
      const {
        evictedKeys
      } = evictLowPriorityData();
      if (evictedKeys.length > 0) {
        try {
          localStorage.setItem(key, value);
          return true;
        } catch {
          logError(`localStorage write failed for "${key}" even after evicting ${evictedKeys.length} keys`);
          return false;
        }
      }
      logError(`localStorage quota exceeded for "${key}" — no evictable keys found. Usage: ${getLocalStorageUsagePercent()}%`);
      return false;
    }
    logError(`Failed to write to localStorage ("${key}"):`, err);
    return false;
  }
}

/**
 * Get a diagnostic summary of localStorage usage.
 * Useful for developer tools / diagnostics page.
 */
export function getStorageDiagnostics(): {
  usedBytes: number;
  usedKB: number;
  estimatedMaxKB: number;
  usagePercent: number;
  isNearFull: boolean;
  topKeys: {
    key: string;
    sizeKB: number;
  }[];
} {
  const usedBytes = getLocalStorageUsedBytes();
  const topKeys: {
    key: string;
    sizeKB: number;
  }[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const size = (key.length + (localStorage.getItem(key)?.length ?? 0)) * 2;
        topKeys.push({
          key,
          sizeKB: Math.round(size / 1024)
        });
      }
    }
    topKeys.sort((a, b) => b.sizeKB - a.sizeKB);
  } catch (err) {
    logError('Failed to build storage diagnostics:', err);
  }
  return {
    usedBytes,
    usedKB: Math.round(usedBytes / 1024),
    estimatedMaxKB: Math.round(ESTIMATED_MAX_BYTES / 1024),
    usagePercent: getLocalStorageUsagePercent(),
    isNearFull: isLocalStorageNearFull(),
    topKeys: topKeys.slice(0, 10)
  };
}

/**
 * Storage key priority tiers for eviction strategy.
 * Lower number = higher priority (never evict).
 */
const EVICTION_PRIORITY: Record<string, number> = {
  // CRITICAL — never evict (tier 0)
  meester_djay_v3: 0,
  'meester-djay-learner-prefs': 0,
  'meester-djay-admin-settings': 0,
  meester_djay_governance_config_v1: 0,
  // HIGH — avoid evicting (tier 1)
  meester_djay_v3_assessment_blueprints: 1,
  meester_djay_v3_assessment_attempts: 1,
  meester_djay_v3_assessment_reports: 1,
  teacher_notes: 1,
  oefenlab_personal_records: 1,
  /*
   AS-2026-07-P2-06 (July 2026): the assignment ledgers were previously
   ABSENT from this map, so `getKeyPriority` defaulted them to tier 2 and
   they were evictable. They hold `completedByStudentIds` /
   `abandonedByStudentIds` — the authoritative record of which learner
   finished which teacher-assigned toets, and (since AS-2026-07-P1-04) the
   source of the teacher's progress counter. Losing them silently rewinds a
   class's homework/toets state. They are durable pedagogical records, not
   caches, so they belong at tier 1 alongside attempts and reports.
   */
  teacher_assignments: 1,
  practice_assignments: 1,
  /*
   AS-2026-07-P2-06: an in-progress assessment is the single most
   time-critical thing in the app — a learner mid-toets. Evicting it
   destroys exactly what the recovery layer exists to protect, so it is
   tier 1. Staleness is already handled correctly and cheaply elsewhere:
   `getRecoverableSession` discards sessions older than
   ASSESSMENT_SESSION_MAX_AGE_MS, and the key holds at most one session.
   */
  meester_djay_v3_assessment_session: 1,
  // MEDIUM — evict if needed (tier 2)
  meester_djay_v3_group: 2,
  meester_djay_v3_period: 2,
  // LOW — evict first (tier 3)
  fluency_intro_seen: 3,
  sprint_intro_seen: 3,
  oefenlab_free_practice_intro_seen: 3,
  oefenlab_challenge_intro_seen: 3,
  // canonical key — see CHALLENGE_INTRO_KEY in dataLayer.ts
  oefenlab_last_session: 3,
  oefenlab_newly_unlocked_challenge: 3,
  klascode_banner_dismissed: 3
};

/**
 * Get the eviction priority tier for a localStorage key.
 *
 * ⚠️ AS-2026-07-P2-06: unknown keys default to tier 2, which means
 * **EVICTABLE**. This is not a safe default for durable data — it is how the
 * assignment ledgers silently became evictable simply by not being listed
 * here. Any new key that holds learner or teacher records MUST be added to
 * `EVICTION_PRIORITY` explicitly at tier 0 or 1. Treat the default as
 * "disposable cache", and prefer registering the key over relying on it.
 */
function getKeyPriority(key: string): number {
  if (EVICTION_PRIORITY[key] !== undefined) return EVICTION_PRIORITY[key];
  /*
   Leerling-gescopede oefenlab-sleutels (`<basis>_<learnerId>`). De map hierboven
   is exact-match, dus zodra deze drie per leerling werden weggeschreven vielen
   ze door naar de tier-2-default en werden ze EVICTABLE — inclusief
   `oefenlab_personal_records`, dat hierboven expliciet op tier 1 staat. Precies
   de fout die de assignment-ledgers eerder trof, in een nieuwe vorm: niet door
   iets te verwijderen, maar door een sleutelnaam te veranderen.
    De prefix-regels staan hier en niet als losse map-ingangen, omdat het aantal
   sleutels met het aantal leerlingen op het apparaat meegroeit.
   */

  if (key.startsWith('oefenlab_personal_records')) return 1;
  if (key.startsWith('oefenlab_last_session')) return 3;
  if (key.startsWith('oefenlab_newly_unlocked_challenge')) return 3;
  // AB test variant keys (ab_test_*) are low priority
  if (key.startsWith('ab_test_')) return 3;
  // Unknown keys default to medium priority (evictable — see warning above)
  return 2;
}

/**
 * Evict low-priority localStorage keys to free up space.
 *
 * Strategy: Remove keys from lowest priority tier first (tier 3),
 * then tier 2 if still needed. Never touches tier 0 (critical) or tier 1 (high).
 *
 * @param targetUsagePercent - Stop evicting when usage drops below this (default: 70%)
 * @returns Object with evicted keys and final usage percentage
 *
 * Addresses: Enterprise Audit H-4 — Storage quota LRU eviction
 */
export function evictLowPriorityData(targetUsagePercent = 70): {
  evictedKeys: string[];
  finalUsagePercent: number;
  freedBytes: number;
} {
  const evictedKeys: string[] = [];
  let freedBytes = 0;
  const startUsage = getLocalStorageUsagePercent();
  if (startUsage <= targetUsagePercent) {
    return {
      evictedKeys,
      finalUsagePercent: startUsage,
      freedBytes
    };
  }

  // Collect all keys with their sizes and priorities
  const keyEntries: {
    key: string;
    priority: number;
    sizeBytes: number;
  }[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const sizeBytes = (key.length + (localStorage.getItem(key)?.length ?? 0)) * 2;
        keyEntries.push({
          key,
          priority: getKeyPriority(key),
          sizeBytes
        });
      }
    }
  } catch (err) {
    logError('Failed to enumerate localStorage keys for eviction:', err);
    return {
      evictedKeys,
      finalUsagePercent: getLocalStorageUsagePercent(),
      freedBytes
    };
  }

  // Sort by priority descending (highest tier number = evict first), then by size descending
  keyEntries.sort((a, b) => b.priority - a.priority || b.sizeBytes - a.sizeBytes);

  // Evict keys, stopping at tier 1 (never evict tier 0 or 1)
  for (const entry of keyEntries) {
    if (entry.priority <= 1) break; // Never evict critical or high-priority keys
    if (getLocalStorageUsagePercent() <= targetUsagePercent) break;
    try {
      localStorage.removeItem(entry.key);
      evictedKeys.push(entry.key);
      freedBytes += entry.sizeBytes;
      warn(`Storage eviction: removed "${entry.key}" (tier ${entry.priority}, ${Math.round(entry.sizeBytes / 1024)}KB)`);
    } catch (err) {
      logError(`Failed to evict key "${entry.key}":`, err);
    }
  }
  const finalUsage = getLocalStorageUsagePercent();
  if (evictedKeys.length > 0) {
    warn(`Storage eviction complete: freed ${Math.round(freedBytes / 1024)}KB, usage ${startUsage}% → ${finalUsage}%`);
  }
  return {
    evictedKeys,
    finalUsagePercent: finalUsage,
    freedBytes
  };
}